import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getExam, createAttempt, submitSpeaking, speakingChat, pollAttemptUntilMarked } from '../api/exams';
import { SpinnerOverlay } from '../components/index.jsx';
import MicButton from '../components/MicButton.jsx';
import QuickCreditTopUp from '../components/QuickCreditTopUp.jsx';
import { useAttempt } from '../context/AttemptContext';
import { useAuth } from '../context/AuthContext';

export default function Speaking() {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const fullExam = searchParams.get('fullExam') === 'true';
  const navigate = useNavigate();
  const { attemptId: sharedAttemptId, setSRes } = useAttempt();
  const { user, refreshUser } = useAuth();

  const [exam, setExam] = useState(null);
  const [messages, setMessages] = useState([]);  // [{role:'ai'|'user', text:'...'}]
  const [apiMessages, setApiMessages] = useState([]);  // format for API
  const [micState, setMicState] = useState('idle');
  const [liveText, setLiveText] = useState('');
  const [testEnded, setTestEnded] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [scoreResult, setScoreResult] = useState(null);
  const [attemptId, setAttemptId] = useState(sharedAttemptId);
  const [part, setPart] = useState('Part 1');
  const [fallbackInput, setFallbackInput] = useState('');
  const [showFallback, setShowFallback] = useState(false);
  const [pageMessage, setPageMessage] = useState('');

  const messagesEndRef = useRef(null);
  const recogRef = useRef(null);
  const recogFinalRef = useRef('');
  const silenceTimerRef = useRef(null);
  const shouldSubmitRef = useRef(false);

  // TTS
  const speak = useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text.replace(/[*_~`#]/g, '').trim());
    utt.lang = 'en-GB'; utt.rate = 0.92; utt.pitch = 1.05;
    const voices = window.speechSynthesis.getVoices();
    const pref = voices.find(v => /en.?GB/i.test(v.lang)) || voices.find(v => /en/i.test(v.lang));
    if (pref) utt.voice = pref;
    window.speechSynthesis.speak(utt);
  }, []);

  const stopSpeaking = () => window.speechSynthesis?.cancel();

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  // Add message to UI
  const addMessage = (role, text) => {
    setMessages(prev => [...prev, { role, text }]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // Send a turn to the AI
  const sendToAI = useCallback(async (userText) => {
    const newApiMsgs = [...apiMessages, { role: 'user', content: userText }];
    setApiMessages(newApiMsgs);
    setMicState('processing');
    stopSpeaking();

    try {
      const { reply } = await speakingChat(attemptId, newApiMsgs);
      setApiMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      addMessage('ai', reply);
      speak(reply);

      // Update part indicator
      const lower = reply.toLowerCase();
      if (lower.includes('part 4') || lower.includes('discussion')) setPart('Part 4');
      else if (lower.includes('part 3') || lower.includes('collaborative')) setPart('Part 3');
      else if (lower.includes('part 2') || lower.includes('photo')) setPart('Part 2');

      // Check for test end
      if (lower.includes('that concludes our speaking test') || lower.includes('well done!')) {
        setTestEnded(true);
        setMicState('disabled');
        addMessage('sys', '✅ All parts complete! Press "Get Score" for your assessment.');
      } else {
        setMicState('idle');
      }
    } catch (err) {
      console.error(err);
      addMessage('sys', '⚠️ Connection issue. Please try again.');
      setMicState('idle');
    }
  }, [apiMessages, attemptId, speak]);

  const submitRecognizedText = useCallback(() => {
    clearSilenceTimer();
    setLiveText('');
    const text = recogFinalRef.current.trim();
    recogFinalRef.current = '';
    recogRef.current = null;
    shouldSubmitRef.current = false;

    if (text.length > 2 && !testEnded) {
      addMessage('user', text);
      sendToAI(text);
    } else {
      setMicState('idle');
    }
  }, [sendToAI, testEnded]);

  // Mic
  const startListening = useCallback(() => {
    if (testEnded) return;
    stopSpeaking();

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setShowFallback(true); return; }

    if (recogRef.current && micState === 'listening') {
      shouldSubmitRef.current = true;
      clearSilenceTimer();
      recogRef.current.stop();
      return;
    }

    const recog = new SR();
    recog.lang = 'en-US'; recog.continuous = true; recog.interimResults = true;
    recogFinalRef.current = '';
    shouldSubmitRef.current = false;
    recogRef.current = recog;
    setLiveText('');
    setMicState('listening');

    const armSilenceTimer = () => {
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        shouldSubmitRef.current = true;
        recog.stop();
      }, 4500);
    };

    recog.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) recogFinalRef.current += e.results[i][0].transcript + ' ';
        else interim = e.results[i][0].transcript;
      }
      setLiveText((recogFinalRef.current + interim).trim() || 'Listening…');
      armSilenceTimer();
    };

    recog.onerror = (e) => {
      clearSilenceTimer();
      setMicState('idle'); setLiveText('');
      recogRef.current = null;
      shouldSubmitRef.current = false;
      if (e.error === 'not-allowed') { addMessage('sys', '⚠️ Mic access denied. Allow microphone and refresh.'); setShowFallback(true); }
      else if (e.error === 'no-speech') addMessage('sys', 'No speech detected. Try again.');
    };

    recog.onend = () => {
      clearSilenceTimer();
      if (shouldSubmitRef.current) {
        submitRecognizedText();
      } else if (!testEnded && micState === 'listening') {
        try {
          recog.start();
        } catch {
          setMicState('idle');
          recogRef.current = null;
        }
      } else {
        setMicState('idle');
        recogRef.current = null;
      }
    };

    recog.start();
  }, [micState, submitRecognizedText, testEnded]);

  // Get final score
  const getScore = async () => {
    setScoring(true);
    stopSpeaking();
    try {
      const transcript = messages.filter(m => m.role !== 'sys').map(m => ({ role: m.role, content: m.text }));
      await submitSpeaking(attemptId, transcript);
      await refreshUser().catch(() => {});
      const stop = pollAttemptUntilMarked(
        attemptId,
        (attempt) => {
          const sr = attempt.speaking_responses[0];
          if (sr?.mark_status === 'done' || sr?.mark_status === 'failed') {
            stop();
            setScoreResult(sr);
            setScoring(false);
            if (fullExam && sr.mark_status === 'done') {
              setSRes({ total: sr.total, band: sr.band, cefr: sr.cefr, strengths: sr.strengths, improvements: sr.improvements, suggestion: sr.suggestion });
            }
          }
        },
        () => {
          setScoring(false);
          addMessage('sys', 'Scoring is taking longer than expected. Please refresh shortly or check your results page.');
        }
      );
    } catch (err) {
      console.error(err); setScoring(false);
    }
  };

  // Init
  useEffect(() => {
    const init = async () => {
      const ex = await getExam(examId);
      setExam(ex);
      let aid = sharedAttemptId;
      if (!aid) {
        const attempt = await createAttempt(examId, fullExam ? 'full_exam' : 'practice', 'speaking');
        aid = attempt.id;
        setAttemptId(aid);
      }
      // Start the conversation
      addMessage('sys', 'Connecting to your AI examiner…');
      try {
        const { reply, credits_charged, start_credit_already_used } = await speakingChat(aid, [{ role: 'user', content: '[Student is ready. Begin the speaking test.]' }]);
        setApiMessages([
          { role: 'user', content: '[Student is ready. Begin the speaking test.]' },
          { role: 'assistant', content: reply }
        ]);
        setMessages([
          ...(credits_charged ? [{ role: 'sys', text: 'ℹ️ 1 AI credit was used to start the speaking examiner. Final scoring will use 1 more credit.' }] : []),
          ...(!credits_charged && start_credit_already_used ? [{ role: 'sys', text: 'ℹ️ Existing speaking session resumed. No new start credit was charged because this session had already used its first credit earlier.' }] : []),
          { role: 'ai', text: reply }
        ]);
        await refreshUser().catch(() => {});
        const speakIt = () => speak(reply);
        if (window.speechSynthesis?.getVoices().length > 0) speakIt();
        else if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = () => { speakIt(); window.speechSynthesis.onvoiceschanged = null; };
        setMicState('idle');
      } catch { addMessage('sys', 'Connection error. Please refresh.'); }
    };
    init().catch(console.error);
    return () => { clearSilenceTimer(); stopSpeaking(); recogRef.current?.stop(); };
  }, []);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const timeout = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timeout);
  }, [pageMessage]);

  if (!exam) return <SpinnerOverlay text="Connecting to examiner…" />;

  return (
    <div className="speak-wrap">
      {pageMessage && (
        <div style={{ position: 'fixed', top: 84, right: 20, zIndex: 40, maxWidth: 380, background: '#111827', color: '#fff', padding: '12px 14px', borderRadius: 12, boxShadow: '0 20px 45px rgba(15,23,42,.28)', border: '1px solid rgba(255,255,255,.08)', fontSize: 14, fontWeight: 600 }}>
          {pageMessage}
        </div>
      )}
      {/* Header */}
      <div className="speak-header">
        <button className="bbt" onClick={() => { stopSpeaking(); navigate(fullExam ? `/exam/${examId}` : `/hub/${examId}`); }}>
          ← {fullExam ? 'Exit Exam' : 'Back'}
        </button>
        <span style={{ color: '#fff', fontSize: 14, fontWeight: 600, flex: 1, textAlign: 'center' }}>🎙 Speaking Test — {exam.title}</span>
        {user && (
          <span style={{ background: 'rgba(245,158,11,.18)', color: '#FEF3C7', fontSize: 12, padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(245,158,11,.28)', fontWeight: 800 }}>
            الرصيد المتبقي: {user.ai_credits ?? 0}
          </span>
        )}
        {user && (
          <QuickCreditTopUp returnTo={`${window.location.pathname}${window.location.search}`} onMessage={setPageMessage} />
        )}
        <span style={{ background: 'rgba(255,255,255,.12)', color: 'rgba(255,255,255,.8)', fontSize: 12, padding: '4px 12px', borderRadius: 20 }}>{part}</span>
        {testEnded && !scoreResult && (
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,.15)', color: '#fff', border: '1px solid rgba(255,255,255,.25)' }} onClick={getScore}>
            ✓ Get Score
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="speak-messages">
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'ai' ? 'msg-ai' : m.role === 'user' ? 'msg-user' : 'msg-sys'}>
            {m.role === 'ai' && <div className="avatar-ai">E</div>}
            {m.role === 'user' && <div className="avatar-user">🎤</div>}
            <div className={m.role === 'ai' ? 'bubble-ai' : m.role === 'user' ? 'bubble-user' : 'bubble-sys'}>
              {m.text}
            </div>
          </div>
        ))}
        {micState === 'processing' && (
          <div className="msg-ai">
            <div className="avatar-ai">E</div>
            <div className="bubble-ai" style={{ padding: '12px 14px' }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[0, .2, .4].map(d => <div key={d} className="typing-dot" style={{ animationDelay: `${d}s` }} />)}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Footer */}
      <div className="speak-footer">
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {liveText && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#15803d', marginBottom: 12, fontStyle: 'italic' }}>
              {liveText}
            </div>
          )}

          {!scoreResult && !scoring && (
            <>
              <MicButton state={micState} onClick={startListening} />
              {showFallback && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--er)', marginBottom: 6 }}>⚠️ Mic unavailable — type your answer:</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <textarea
                      className="fc"
                      value={fallbackInput}
                      onChange={e => setFallbackInput(e.target.value)}
                      style={{ minHeight: 44, maxHeight: 100, resize: 'none' }}
                      rows={2}
                      placeholder="Type your response…"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (fallbackInput.trim()) { addMessage('user', fallbackInput.trim()); sendToAI(fallbackInput.trim()); setFallbackInput(''); } } }}
                    />
                    <button className="btn btn-teal" onClick={() => { if (fallbackInput.trim()) { addMessage('user', fallbackInput.trim()); sendToAI(fallbackInput.trim()); setFallbackInput(''); } }}>Send →</button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={stopSpeaking}>🔇 Stop audio</button>
                {testEnded && <button className="btn btn-ghost btn-sm" onClick={getScore}>End & Get Score</button>}
              </div>
            </>
          )}

          {scoring && <SpinnerOverlay text="Calculating your B1 speaking score…" />}

          {/* Score result */}
          {scoreResult && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg,#064e3b,var(--tl))', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ color: '#fff' }}><div style={{ fontSize: 11, opacity: .7, marginBottom: 3 }}>🎙 Speaking Assessment</div><div style={{ fontSize: 24, fontWeight: 700 }}>{scoreResult.total}/15</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>Band</div><div style={{ fontSize: 36, fontWeight: 700, color: '#6ee7b7', lineHeight: 1 }}>{scoreResult.band}</div></div>
                <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8, padding: '8px 14px', textAlign: 'center', color: '#fff' }}><div style={{ fontSize: 10, opacity: .7 }}>CEFR</div><div style={{ fontSize: 14, fontWeight: 700 }}>{scoreResult.cefr}</div></div>
              </div>
              <div style={{ padding: '14px 18px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
                  {[['Grammar & Vocab', scoreResult.scores?.grammar], ['Discourse', scoreResult.scores?.discourse], ['Communication', scoreResult.scores?.interaction]].map(([l, v]) => (
                    <div key={l} className="criteria-box"><div className="criteria-val" style={{ color: 'var(--tl)' }}>{v ?? '—'}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--mu)' }}>/5</span></div><div className="criteria-name">{l}</div></div>
                  ))}
                </div>
                <div className="result-feedback" style={{ borderLeft: '3px solid var(--tl)' }}>
                  <p>✅ <strong>ما الذي تتقنه بالفعل في مستواك الحالي:</strong> {scoreResult.strengths}</p>
                  <p>⚠️ <strong>أهم خطوة تالية لتحسين مستواك:</strong> {scoreResult.improvements}</p>
                  <p>💡 <strong>جرّب هذا في إجابتك القادمة:</strong> {scoreResult.suggestion}</p>
                </div>
                {fullExam ? (
                  <button className="btn btn-gold btn-lg btn-full" style={{ marginTop: 14 }} onClick={() => navigate(`/reading/${examId}?fullExam=true`)}>
                    Continue to Reading 📖 →
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => navigate(`/hub/${examId}`)}>← Back to Hub</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
